import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {Link} from 'react-router';
import {FLOW_STEP_TYPES} from '../../common/model-api/FlowStepDefType';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import AppPanel from '../fw/AppPanel.react';
import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';
import {injectIntl, defineMessages} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import RefreshButton from './../jobs/RefreshButton.react';
import {FormattedRelative, FormattedMessage, FormattedDate, FormattedTime, IntlShape} from 'react-intl';
import * as BrowserPermission from "../BrowserPermission";
import * as Permission from "../../common/util/Permission";
import {getLastOrganisationId} from '../app/LastOrganisationId';
import FlowInvocationStatus from './FlowInvocationStatus.react';

const messages = defineMessages({
    title: {id: 'flowInvocationsList.title', 'defaultMessage': 'Flow Invocations'},
    createdAtAfter: {id: 'flowInvocationsList.createdAtAfter', 'defaultMessage': 'Created after'},
    createdAtBefore: {id: 'flowInvocationsList.createdAtBefore', 'defaultMessage': 'Created before'},
    createdAt: {id: 'flowInvocationsList.createdAt', 'defaultMessage': 'Created at'},
    filteredByIncomingEmail: {id: 'flowInvocationsList.filteredByIncomingEmail', 'defaultMessage': 'Showing only invocations for this incoming email.'},
    backToIncomingEmail: {id: 'flowInvocationsList.backToIncomingEmail', 'defaultMessage': 'Back to incoming email'},
    incomingEmailSubject: {id: 'flowInvocationsList.incomingEmailSubject', 'defaultMessage': 'Subject'},
    incomingEmailSender: {id: 'flowInvocationsList.incomingEmailSender', 'defaultMessage': 'Sender'},
    incomingEmailCreatedAt: {id: 'flowInvocationsList.incomingEmailCreatedAt', 'defaultMessage': 'Received at'},
});

class FlowInvocationList extends React.Component {

    typeName = 'flowInvocations';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired
    };

	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			loadingError: false,
			sortDesc: true,
		};
	}

    search = (event) => {
		this.setState({
			loading: true,
			loadingError: false,
		});

		let values = {};

		if (this.refs.createdAtBeforeBoxRef.value) {
			let time = this.refs.createdAtBeforeTimeBoxRef.value || "23:59:59";
			values["createdAtBefore"] = this.refs.createdAtBeforeBoxRef.value + " " + time;
		}
		if (this.refs.createdAtAfterBoxRef.value) {
			let time = this.refs.createdAtAfterTimeBoxRef.value || "00:00:00";
            values["createdAtAfter"] = this.refs.createdAtAfterBoxRef.value + " " + time;
		}

		this.props.relay.setVariables(values, (state) => {
			if (state.done) {
				this.setState({
					loading: false,
					loadingError: false,
				});
			} else if (state.error) {
				this.setState({
					loading: false,
					loadingError: true,
				});
			}
		});
	};

	reset = () => {
		if (this.refs.createdAtAfterBoxRef) {
			this.refs.createdAtAfterBoxRef.value = "";
		}
		if (this.refs.createdAtBeforeBoxRef) {
			this.refs.createdAtBeforeBoxRef.value = "";
		}
		if (this.refs.createdAtAfterTimeBoxRef) {
			this.refs.createdAtAfterTimeBoxRef.value = "";
		}
		if (this.refs.createdAtBeforeTimeBoxRef) {
			this.refs.createdAtBeforeTimeBoxRef.value = "";
		}
		this.props.relay.setVariables({
			createdAtAfter: "",
            createdAtBefore: "",
            failed: null,
            startType: null,
		});
	};

    toggleSort = (sortDesc) => {
        this.setState({ loading: true, loadingError: false, sortDesc });
        const values = {
            after: null,
            before: null,
            first: defaultPageSize,
            last: null,
            orderBy: sortDesc ? 'createdAt desc' : 'createdAt asc',
        };
        const applyDone = (state) => {
            if (state.done) {
                this.setState({ loading: false });
            } else if (state.error) {
                this.setState({ loading: false, loadingError: true });
            }
        };
        if (this.props.relay && this.props.relay.setVariables) {
            this.props.relay.setVariables(values, applyDone);
        } else if (this.props.relay && this.props.relay.forceFetch) {
            this.props.relay.forceFetch(values, applyDone);
        }
    };

    listUrlPart = 'list';

    render() {
        let { user } = this.props;
        let data = user.organisation.flow;
        let list = data.invocations;
        let type = types[this.typeName];
        let filteredIncomingEmail = data.flowIncomingEmail;
        let isIncomingEmailFiltered = !!this.props.relay.variables.flowIncomingEmailId;

        let currentStartType = this.props.relay.variables.startType ? this.props.relay.variables.startType : 'all';
        let currentFailType = this.props.relay.variables.failed === null ? "-all" :
            (this.props.relay.variables.failed ? '-fail' : '-success');

        return (
            <AppPanel breadcrumbParams={[
                {type: types['flows'], data: data},
                {type: types['organisations'], data: this.props.user.organisation}
            ]} className="fetchList" title={this.props.intl.formatMessage(messages.title)}>
				<h3><FormattedMessage defaultMessage="Flow Invocation History" id="flow.flowInvocationTitle" /></h3>
                <div className="btn-toolbar" role="toolbar">
                    <RefreshButton autoRefresh relay={this.props.relay} />

                    {!isIncomingEmailFiltered ? (
                        <div>
        					<div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
        						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-all', {...data, startType: currentStartType})}><FormattedMessage defaultMessage="All" id="invocationList.listAll" /></Link>
        						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-success', {...data, startType: currentStartType})}><FormattedMessage defaultMessage="Succeeded" id="invocationList.listSuccess" /></Link>
        						<Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-fail', {...data, startType: currentStartType})}><FormattedMessage defaultMessage="Failed" id="invocationList.listFailed" /></Link>
        					</div>

                            <div aria-label="..." className="btn-group hidden-xs hidden-sm" role="group" style={{paddingRight: '1em'}}>
                                <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + currentFailType, {...data, startType: 'all'})}><FormattedMessage defaultMessage="All" id="invocationList.listAll" /></Link>
                                {Object.keys(FLOW_STEP_TYPES).filter((a) => FLOW_STEP_TYPES[a].canBeStart).map((keyType, idx) => {
                                    let step = FLOW_STEP_TYPES[keyType];
                                    return (<Link activeClassName="active" className="btn btn-default" key={idx} onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + currentFailType, {...data, startType: keyType})}>{step.name}</Link>);
                                })}
                            </div>
                        </div>
                    ) : null}

					<div className="input-group" style={{maxWidth: '200px'}}>
						<input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtAfter)} ref="createdAtAfterBoxRef" type="date" />
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtAfter)} ref="createdAtAfterTimeBoxRef" style={{width: '5em'}} type="time" />
					</div>
					<div className="input-group" style={{maxWidth: '200px'}}>
						<input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtBefore)} ref="createdAtBeforeBoxRef" type="date" />
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.createdAtBefore)} ref="createdAtBeforeTimeBoxRef" style={{width: '5em'}} type="time" />
					</div>
					<div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
						<div className="input-group-btn">
							<button className="btn btn-outline-secondary" onClick={this.search} type="button">{this.state.loading ? <span className="fa fa-refresh fa-spin"/> : (this.state.loadingError ? <span className="fa fa-exclamation" style={{color: 'red'}}/> : <span className="fa fa-search"/>)}</button>
						</div>
						<div className="input-group-btn">
							<button className="btn btn-outline-secondary" onClick={this.reset} type="button"><span className="fa fa-times"/></button>
						</div>
					</div>
                </div>

                {isIncomingEmailFiltered ? (
                    <div className="alert alert-info" style={{marginTop: '1em'}}>
                        <div><FormattedMessage {...messages.filteredByIncomingEmail} /></div>
                        {filteredIncomingEmail ? (
                            <div style={{marginTop: '0.5em'}}>
                                <strong>{this.props.intl.formatMessage(messages.incomingEmailSubject)}:</strong> {filteredIncomingEmail.emailSubject || ' - '}<br />
                                <strong>{this.props.intl.formatMessage(messages.incomingEmailSender)}:</strong> {filteredIncomingEmail.emailSender || ' - '}<br />
                                <strong>{this.props.intl.formatMessage(messages.incomingEmailCreatedAt)}:</strong> <FormattedDate value={filteredIncomingEmail.createdAt} /> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={filteredIncomingEmail.createdAt} />
                            </div>
                        ) : null}
                        <div style={{marginTop: '0.5em'}}>
                            <Link className="btn btn-default btn-sm" to={urlMapping.getUrl(types['flowIncomingEmails'], 'view', {
                                organisation_id: data.organisation_id,
                                flow_id: data.realId,
                                id: (filteredIncomingEmail && (filteredIncomingEmail.realId || filteredIncomingEmail.id)) || this.props.relay.variables.flowIncomingEmailId,
                            })}>
                                <FormattedMessage {...messages.backToIncomingEmail} />
                            </Link>
                        </div>
                    </div>
                ) : null}

                <PagingGrid
                        columns={{
                            'startType': {},
                            'flowStepDef.name': {},
                            'createdAt': {
                                header: (
                                    <span onClick={() => { if (!(this.state.loading || (this.props.relay && this.props.relay.pendingVariables))) this.toggleSort(!this.state.sortDesc); }} style={{cursor: (this.state.loading || (this.props.relay && this.props.relay.pendingVariables)) ? 'not-allowed' : 'pointer'}}>
                                        <FormattedMessage {...messages.createdAt} />
                                        {this.state.loading ? <span className="fa fa-refresh fa-spin" style={{marginLeft: '0.5em'}}/> : (
                                            this.state.sortDesc ? <span className="fa fa-long-arrow-down" style={{marginLeft: '0.5em'}}/> : <span className="fa fa-long-arrow-up" style={{marginLeft: '0.5em'}}/>
                                        )}
                                    </span>
                                ),
                                render: (column, item) => {
                                    return renderTime(item, column);
                                }
                            },
                            'priority': {
                            },
                            'lastRun': {
								render: (column, item) => {
                                    return renderTime(item, column);
								}
							},
                            'apiCount': {},
                            'currentProgress': {
                                header: <span></span>,
								render: (column, item) => {
                                    return (<FlowInvocationStatus flowInvocation={item} />);
								}
							},
                        }}
                        edges={list}
                        metadata={type}
                        pageInfo={list.pageInfo}
                        relay={this.props.relay}
                        urlMapping={urlMapping}
                    />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowInvocationList), (props) => {
    return props.user.organisation && props.user.organisation.flow;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        flowIncomingEmailId: null,
        first: defaultPageSize,
        after: null,
        before: null,
        last: null,
        failed: null,
        startType: null,
		createdAtBefore: null,
		createdAtAfter: null,
        orderBy: 'createdAt desc',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
        }

        processPagingParams(variables);

        if (variables.failed) {
            if (variables.failed === "all") {
                variables.failed = null;
            } else if (variables.failed === "fail" || variables.failed === true) {
				variables.failed = true;
			} else {
				variables.failed = false;
			}
		}
        if (variables.startType) {
            if (variables.startType === "all") {
                variables.startType = null;
            }
        }
        if (!variables.flowIncomingEmailId) {
            variables.flowIncomingEmailId = null;
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        flowIncomingEmail(id: $flowIncomingEmailId) {
                            id,
                            realId,
                            flow_id,
                            organisation_id,
                            emailSubject,
                            emailSender,
                            createdAt,
                        },
                        flowDef {
                            id,
                            realId,
                            name,
                        },
                        invocations(first: $first, after: $after, before: $before, last: $last, failed: $failed, startType: $startType, flowIncomingEmail_id: $flowIncomingEmailId, createdAtBefore: $createdAtBefore, createdAtAfter: $createdAtAfter, orderBy: $orderBy) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    organisation_id,
                                    flow_id,
                                    startType,
                                    apiCount,
                                    flowStepDef {
                                        id,
                                        realId,
                                        name,
                                    },
                                    ${FlowInvocationStatus.getFragment("flowInvocation")},
                                    nextRetry,
                                    lastRun,
                                    priority,
                                    createdAt,
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        }
                    }
                }
            }
        `,
    },
});

export function renderTime(item, column) {
    if (!item[column]) {
        return '';
    }

    return (
        <span>
            <span title={(new Date(item[column])).toISOString()}>
                <FormattedDate value={item[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={item[column]}/>&nbsp;-&nbsp;
            </span>
            <span title={(new Date(item[column])).toISOString()}>
                <FormattedRelative value={item[column]}/>
            </span>
        </span>
    );
}
